import Bugsnag, { Event } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { CONFIG } from '@/constants/config';
import { isJestRunning } from '@/utils/helpers';

type IgnoreRuleConfig = {
  errorClass?: string | string[];
  messageIncludes?: string[];
  stackIncludes?: RegExp[];
};

const NOOP_BUGSNAG_API_KEY = 'DO_NOT_SEND';

const IGNORE_RULES: IgnoreRuleConfig[] = [
  {
    errorClass: 'TypeError',
    messageIncludes: ['null', 'setItem'],
    stackIncludes: [/zustand/i, /persist/i],
  },
];

function getEventMessage(event: Event): string | undefined {
  const error = event.errors[0];
  if (error?.errorMessage) {
    return error.errorMessage;
  }
  if (event.originalError instanceof Error) {
    return event.originalError.message;
  }
  return undefined;
}

function eventStackIncludes(event: Event, pattern: RegExp): boolean {
  const frames = event.errors[0]?.stacktrace;
  if (!frames?.length) return false;
  return frames.some(frame =>
    pattern.test(`${frame.file ?? ''} ${frame.method ?? ''}`)
  );
}

function buildRuleMatchers(
  rule: IgnoreRuleConfig
): Array<(event: Event) => boolean> {
  const matchers: Array<(event: Event) => boolean> = [];
  const { errorClass, messageIncludes, stackIncludes } = rule;
  if (errorClass) {
    const allowed = Array.isArray(errorClass) ? errorClass : [errorClass];
    matchers.push(event => allowed.includes(event.errors[0]?.errorClass ?? ''));
  }
  if (messageIncludes?.length) {
    const parts = messageIncludes;
    matchers.push(event => {
      const message = getEventMessage(event);
      return !!message && parts.every(part => message.includes(part));
    });
  }
  if (stackIncludes?.length) {
    const patterns = stackIncludes;
    matchers.push(event =>
      patterns.some(pattern => eventStackIncludes(event, pattern))
    );
  }
  return matchers;
}

function matchesIgnoreRule(event: Event, rule: IgnoreRuleConfig): boolean {
  const matchers = buildRuleMatchers(rule);
  return matchers.length > 0 && matchers.every(match => match(event));
}

function shouldIgnoreBugsnagEvent(event: Event): boolean {
  return IGNORE_RULES.some(rule => {
    try {
      return matchesIgnoreRule(event, rule);
    } catch {
      return false;
    }
  });
}

class NoopBugsnag {
  notify() {
    console.log('NoopBugsnag - notify', this);
  }

  isStarted() {
    console.log('NoopBugsnag - isStarted', this);
    return false;
  }
}

export function startBugsnag() {
  return isJestRunning() || CONFIG.bugsnagApiKey === NOOP_BUGSNAG_API_KEY
    ? new NoopBugsnag()
    : Bugsnag.start({
        apiKey: CONFIG.bugsnagApiKey,
        onError(event) {
          if (shouldIgnoreBugsnagEvent(event)) {
            return false;
          }
          const reportApp = event.app;
          const requestUrl = event.request.url;
          if (requestUrl?.match(/(?:.+\.)?tap\.az/g)) {
            reportApp.releaseStage = 'production';
            reportApp.notifyReleaseStages = 'production';
          } else {
            reportApp.releaseStage = process.env.VERCEL_GITHUB_BRANCH;
            reportApp.notifyReleaseStages = process.env.VERCEL_GITHUB_BRANCH;
          }
        },
        plugins: [new BugsnagPluginReact()],
      });
}

export const bugsnag = startBugsnag();
